import { useState, useEffect } from "react";
import getConfig from "next/config";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Navigation } from "swiper/modules";

import CarouselNavArrow from "@components/Icons/CarouselNavArrow";

import "swiper/css";
import "swiper/css/navigation";

const {
  publicRuntimeConfig: {
    settings: { cdn, websiteName },
  },
} = getConfig();

// Brico: shop-in-shop tiles get rounded corners to match the brand's rounded
// card motif. These tiles carry no `rounded-*` class, so the centralized brico
// rounding CSS never reaches them — apply it here. Altex/MG keep square tiles.
const tileRadius = websiteName === "bricostore" ? "rounded-[8px]" : "";

const PreviousButton = ({ setPrevEl, isBeginning }) => (
  <div
    ref={(node) => setPrevEl(node)}
    className={`w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-1300 ${
      isBeginning ? "opacity-50 cursor-default" : "opacity-90 cursor-pointer"
    }`}
  >
    <CarouselNavArrow />
  </div>
);

const NextButton = ({ setNextEl, isEnd }) => (
  <div
    ref={(node) => setNextEl(node)}
    className={`w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-1300 ${
      isEnd ? "opacity-50 cursor-default" : "opacity-90 cursor-pointer"
    }`}
  >
    <CarouselNavArrow direction="right" />
  </div>
);

const ShopInShopBanners = ({ banners }) => {
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  // Use state (not refs) for the nav elements: setting the node triggers a
  // re-render so Swiper re-initializes navigation with the real DOM button.
  // With refs, `.current` is still null when Swiper first inits (the buttons
  // mount after the Swiper) and the nav never binds — buttons do nothing.
  const [prevEl, setPrevEl] = useState(null);
  const [nextEl, setNextEl] = useState(null);

  const isSingleSlide = Boolean(banners.length === 1);

  useEffect(() => {
    banners.map((banner) => {
      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.

      window.dataLayer.push({
        event: "view_promotion",
        ecommerce: {
          promotion_name: "Homepage Shop-in-Shop banners",
          creative_name: banner.url,
          creative_slot: banner.index,
        },
      });
    });
  }, [banners]);

  const trackBannerClick = (banner) => {
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.

    window.dataLayer.push({
      event: "select_promotion",
      ecommerce: {
        promotion_name: "Homepage Shop-in-Shop banners",
        creative_name: banner.url,
        creative_slot: banner.index,
      },
    });
  };

  return (
    <div className="relative">
      {/* Mobile view swiper */}
      <Swiper
        className="sm:hidden"
        slidesPerView="auto"
        slidesPerGroup={2}
        spaceBetween={10}
        modules={[Navigation, FreeMode]}
        onSlideChange={({ isBeginning, isEnd }) => {
          setIsBeginning(isBeginning);
          setIsEnd(isEnd);
        }}
        navigation={{
          nextEl,
          prevEl,
        }}
      >
        {banners.map((banner) => (
          <SwiperSlide key={banner.index} className="max-w-fit">
            <Link href={banner.url}>
              <a title="" onClick={() => trackBannerClick(banner)}>
                <img
                  src={`${cdn}${banner.file_mobile}`}
                  alt=""
                  className={`aspect-square w-[150px] max-w-[150px] ${tileRadius}`}
                />
              </a>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Tablet view swiper */}
      <Swiper
        className="hidden sm:block md:hidden"
        slidesPerView="auto"
        slidesPerGroup={2}
        spaceBetween={10}
        modules={[Navigation, FreeMode]}
        onSlideChange={({ isBeginning, isEnd }) => {
          setIsBeginning(isBeginning);
          setIsEnd(isEnd);
        }}
        navigation={{
          nextEl,
          prevEl,
        }}
      >
        {banners.map((banner) => (
          <SwiperSlide key={banner.index} className="max-w-fit">
            <Link href={banner.url}>
              <a title="" onClick={() => trackBannerClick(banner)}>
                <img
                  src={`${cdn}${banner.file_tablet}`}
                  alt=""
                  className={`aspect-square w-[180px] max-w-[180px] ${tileRadius}`}
                />
              </a>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Desktop view swiper */}
      <Swiper
        className="hidden md:block"
        slidesPerView="auto"
        slidesPerGroup={2}
        spaceBetween={10}
        modules={[Navigation, FreeMode]}
        onSlideChange={({ isBeginning, isEnd }) => {
          setIsBeginning(isBeginning);
          setIsEnd(isEnd);
        }}
        navigation={{
          nextEl,
          prevEl,
        }}
      >
        {banners.map((banner) => (
          <SwiperSlide key={banner.index} className="max-w-fit">
            <Link href={banner.url}>
              <a title="" onClick={() => trackBannerClick(banner)}>
                <img
                  src={`${cdn}${banner.file}`}
                  alt=""
                  className={`aspect-square w-[232px] max-w-[232px] ${tileRadius}`}
                />
              </a>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>

      {!isSingleSlide && (
        <>
          <div className="absolute left-[10px] z-10 top-[40%]">
            <PreviousButton setPrevEl={setPrevEl} isBeginning={isBeginning} />
          </div>

          <div className="absolute right-[10px] z-10 top-[40%]">
            <NextButton setNextEl={setNextEl} isEnd={isEnd} />
          </div>
        </>
      )}
    </div>
  );
};

export default ShopInShopBanners;
